import type { EntranceConnection } from '../../data/entrance-types';
import { ONE_WAY_ENTRANCE_IDS, VANILLA_TO_DEAD_END } from '../../data/entrance-types';
import type { PathEdge, PathGraph, LogicNode } from '../types';
import { getLogicData, resolveZone } from '../logic/logic-data';
import { parseExpression } from '../expression/evaluator';
import { REGION_TO_ZONE } from '../logic/region-zone-map';
import { ALL_VANILLA_ENTRANCES } from '../../data/vanilla-entrances';

/**
 * Zones that share the same physical overworld navigation.
 * HC (child Castle) and OGC (adult Castle) are visually the same place;
 * tracking one overworld entrance propagates to its twin.
 */
const TWIN_ZONES: Record<string, string> = {
  HC: 'OGC',
  OGC: 'HC',
};

/**
 * Warp song entrance IDs -> OoTR song requirement.
 * Warp songs can be used from ANY zone, so they create global edges.
 */
const WARP_SONG_REQUIREMENTS: Record<string, string> = {
  'Warps::Minuet of Forest':  'can_play(Minuet_of_Forest)',
  'Warps::Bolero of Fire':    'can_play(Bolero_of_Fire)',
  'Warps::Serenade of Water': 'can_play(Serenade_of_Water)',
  'Warps::Nocturne of Shadow':'can_play(Nocturne_of_Shadow)',
  'Warps::Requiem of Spirit': 'can_play(Requiem_of_Spirit)',
  'Warps::Prelude of Light':  'can_play(Prelude_of_Light)',
};

/**
 * Resolve the arrival entrance label at the destination side.
 * For linked entrances: the linked entrance's label (that's what you walk out of).
 * For non-linked (dead-end/destination picker): find the matching vanilla entrance label.
 */
function resolveArrivalLabel(
  conn: EntranceConnection,
  connections: Record<string, EntranceConnection>,
): string | undefined {
  // Two-click linked: the arrival is the linked entrance
  if (conn.isTracked && conn.linkedTo && connections[conn.linkedTo]) {
    const linked = connections[conn.linkedTo];
    return `${linked.zone} ${linked.label}`;
  }
  // Tracked to a destination name (dead-end or destination picker):
  // find which vanilla entrance has this vanillaTo to get a readable label
  // Skip boss rooms — they don't add navigation info
  if (conn.isTracked && conn.actualDestination) {
    const match = ALL_VANILLA_ENTRANCES.find(
      e => e.vanillaTo === conn.actualDestination && e.category !== 'boss',
    );
    if (match) {
      return `${match.zone} ${match.label}`;
    }
    // If only boss room matched, don't show it
    const anyMatch = ALL_VANILLA_ENTRANCES.find(e => e.vanillaTo === conn.actualDestination);
    if (anyMatch?.category === 'boss') return undefined;
    return conn.actualDestination;
  }
  return undefined;
}

/** Map from vanillaTo destination name to a zone code */
function resolveDestinationZone(destination: string): string | undefined {
  // Direct match in region map
  const zone = REGION_TO_ZONE[destination];
  if (zone && !zone.startsWith('_')) return zone;

  // Try matching against vanilla entrance vanillaTo -> zone
  // Exclude boss rooms: they are dead-ends, not navigable zones
  const vanilla = ALL_VANILLA_ENTRANCES.find(
    e => e.vanillaTo === destination && e.category !== 'boss',
  );
  if (vanilla) return vanilla.zone;

  return undefined;
}

/**
 * Build a cross-zone exit lookup: maps "fromZone->toZone" to the OoTR requirement rule.
 * Multiple exits between same zones are collected (we keep all of them as alternative routes).
 */
function buildExitRuleLookup(): Map<string, { rule: string; label: string }[]> {
  const { exits } = getLogicData();
  const lookup = new Map<string, { rule: string; label: string }[]>();

  for (const exit of exits) {
    const fromZone = resolveZone(exit.from);
    const toZone = resolveZone(exit.to);
    if (!fromZone || !toZone || fromZone === toZone) continue;

    const key = `${fromZone}->${toZone}`;
    if (!lookup.has(key)) lookup.set(key, []);
    lookup.get(key)!.push({
      rule: exit.rule,
      label: `${exit.from} => ${exit.to}`,
    });
  }

  return lookup;
}

/**
 * Find the best matching requirement for a vanilla entrance transition.
 * Tries to match by fromZone + toZone, picking the simplest rule (shortest).
 */
function findRequirement(
  fromZone: string,
  toZone: string,
  exitLookup: Map<string, { rule: string; label: string }[]>,
): { rule: string; label: string } | null {
  const key = `${fromZone}->${toZone}`;
  const matches = exitLookup.get(key);
  if (!matches || matches.length === 0) return null;

  // If there's only one, use it
  if (matches.length === 1) return matches[0];

  // Prefer "True" rules (always accessible)
  const trueRule = matches.find(m => m.rule === 'True');
  if (trueRule) return trueRule;

  // Otherwise return the shortest (simplest) rule
  return matches.sort((a, b) => a.rule.length - b.rule.length)[0];
}

/**
 * Build the pathing graph from tracked entrances + logic requirements.
 *
 * @param connections All entrance connections from the tracker state
 * @param allZones List of all zone codes to create warp edges for
 */
export function buildPathGraph(
  connections: Record<string, EntranceConnection>,
  allZones: string[],
): PathGraph {
  const edges: PathEdge[] = [];
  const exitLookup = buildExitRuleLookup();

  /**
   * Push an edge, and if it is an overworld edge involving a twin zone (HC/OGC),
   * also push parallel edges so the tracked navigation is shared between twins.
   */
  function pushEdge(edge: PathEdge) {
    edges.push(edge);
    if (edge.category !== 'overworld') return;
    const twinFrom = TWIN_ZONES[edge.fromZone];
    const twinTo = TWIN_ZONES[edge.toZone];
    if (twinFrom && twinFrom !== edge.toZone) {
      edges.push({ ...edge, fromZone: twinFrom });
    }
    if (twinTo && edge.fromZone !== twinTo) {
      edges.push({ ...edge, toZone: twinTo });
    }
    if (twinFrom && twinTo && twinFrom !== twinTo) {
      edges.push({ ...edge, fromZone: twinFrom, toZone: twinTo });
    }
  }

  for (const conn of Object.values(connections)) {
    if (conn.isReverse) continue;
    if (!conn.isTracked) continue;

    const fromZone = conn.zone;

    // ─── Determine destination name (for dead-end lookup) ──
    let destName: string;
    if (conn.linkedTo && connections[conn.linkedTo]) {
      destName = connections[conn.linkedTo].vanillaTo;
    } else if (conn.actualDestination) {
      destName = conn.actualDestination;
    } else {
      continue; // tracked but no destination set yet — skip
    }
    const deadEndId = VANILLA_TO_DEAD_END.get(destName);

    // ─── Boss rooms: only create dead-end edges (no zone navigation) ──
    if (conn.category === 'boss') {
      if (deadEndId) {
        const arrivalLabel = resolveArrivalLabel(conn, connections);
        edges.push({
          fromZone,
          toZone: deadEndId,
          label: conn.label,
          arrivalLabel,
          requirement: null,
          category: 'boss',
          entranceId: conn.id,
          oneWay: true,
        });
      }
      continue;
    }

    // Determine destination zone
    // For two-click linked entrances, the destination zone is the LINKED entrance's zone
    // (e.g., "GY => KAK" linked to "HF::HF => KAK" means destination is HF, not KAK)
    let toZone: string | undefined;
    if (conn.linkedTo && connections[conn.linkedTo]) {
      toZone = connections[conn.linkedTo].zone;
    } else if (conn.actualDestination) {
      toZone = resolveDestinationZone(conn.actualDestination);
    }

    // Resolve arrival label: the entrance name at the destination side
    const arrivalLabel = resolveArrivalLabel(conn, connections);

    // ─── Warp Songs: global edges from all zones ──────────
    if (conn.category === 'warp' && conn.id in WARP_SONG_REQUIREMENTS) {
      const rule = WARP_SONG_REQUIREMENTS[conn.id];
      let requirement: LogicNode | null = null;
      try {
        requirement = parseExpression(rule);
      } catch { /* noop */ }

      // Warp dead-end edge (e.g., Prelude of Light → de:ToT)
      if (deadEndId) {
        for (const srcZone of ['Warps', ...allZones]) {
          edges.push({
            fromZone: srcZone,
            toZone: deadEndId,
            label: conn.label,
            arrivalLabel,
            requirement,
            category: 'warp',
            entranceId: conn.id,
            oneWay: true,
          });
        }
      }

      if (!toZone || toZone.startsWith('_')) continue;

      // Edge from "Warps" virtual zone (for pathfinding from Warps)
      edges.push({
        fromZone: 'Warps',
        toZone,
        label: conn.label,
        arrivalLabel,
        requirement,
        category: 'warp',
        entranceId: conn.id,
        oneWay: true,
      });
      // Edges from every regular zone (warps can be used from anywhere)
      for (const srcZone of allZones) {
        if (srcZone === toZone) continue;
        edges.push({
          fromZone: srcZone,
          toZone,
          label: conn.label,
          arrivalLabel,
          requirement,
          category: 'warp',
          entranceId: conn.id,
          oneWay: true,
        });
      }
      continue;
    }

    // ─── Spawns: only if explicitly tracked (no vanilla fallback) ──
    if (conn.category === 'spawn') {
      if (!conn.isTracked) continue;
      const spawnReq = conn.id === 'Spawns::Child Spawn' ? 'is_child' : 'is_adult';
      const requirement = parseExpression(spawnReq);

      // Spawn dead-end edge
      if (deadEndId) {
        edges.push({
          fromZone: 'Spawns',
          toZone: deadEndId,
          label: conn.label,
          arrivalLabel,
          requirement,
          category: 'spawn',
          entranceId: conn.id,
          oneWay: true,
        });
      }

      if (toZone && !toZone.startsWith('_')) {
        edges.push({
          fromZone: 'Spawns',
          toZone,
          label: conn.label,
          arrivalLabel,
          requirement,
          category: 'spawn',
          entranceId: conn.id,
          oneWay: true,
        });
      }
      continue;
    }

    // ─── Owl Flights: one-way, child only, only if tracked ──
    if (conn.category === 'owl') {
      if (!conn.isTracked) continue;
      const owlOrigins: Record<string, string> = {
        'Owls::DMT Owl Flight': 'DMT',
        'Owls::LH Owl Flight': 'LH',
      };
      const originZone = owlOrigins[conn.id];
      if (originZone) {
        const requirement = parseExpression('is_child');

        if (deadEndId) {
          edges.push({
            fromZone: originZone,
            toZone: deadEndId,
            label: conn.label,
            arrivalLabel,
            requirement,
            category: 'owl',
            entranceId: conn.id,
            oneWay: true,
          });
        }

        if (toZone && !toZone.startsWith('_')) {
          edges.push({
            fromZone: originZone,
            toZone,
            label: conn.label,
            arrivalLabel,
            requirement,
            category: 'owl',
            entranceId: conn.id,
            oneWay: true,
          });
        }
      }
      continue;
    }

    // Skip zone edge if destination zone is invalid or same as source
    if (!toZone || toZone.startsWith('_') || fromZone === toZone) {
      // Still create dead-end edge even if zone edge is skipped
      if (deadEndId) {
        edges.push({
          fromZone,
          toZone: deadEndId,
          label: conn.label,
          arrivalLabel,
          requirement: null,
          category: conn.category,
          entranceId: conn.id,
          oneWay: true,
        });
      }
      continue;
    }

    // ─── Overworld + other categories ─────────────────────
    const isOneWay = ONE_WAY_ENTRANCE_IDS.has(conn.id);

    // Find the logic requirement for this transition
    const match = findRequirement(fromZone, toZone, exitLookup);
    let requirement: LogicNode | null = null;
    let label = `${fromZone} => ${toZone}`;

    if (match) {
      try {
        requirement = match.rule === 'True' ? null : parseExpression(match.rule);
      } catch { /* noop */ }
      label = match.label;
    }

    pushEdge({
      fromZone,
      toZone,
      label: conn.label || label,
      arrivalLabel,
      requirement,
      category: conn.category,
      entranceId: conn.id,
      oneWay: isOneWay,
    });

    // Dead-end edge alongside the zone edge
    if (deadEndId) {
      edges.push({
        fromZone,
        toZone: deadEndId,
        label: conn.label || label,
        arrivalLabel,
        requirement,
        category: conn.category,
        entranceId: conn.id,
        oneWay: true,
      });
    }

  }

  // ─── Build adjacency list ──────────────────────────────
  const adjacency = new Map<string, PathEdge[]>();
  for (const edge of edges) {
    if (!adjacency.has(edge.fromZone)) adjacency.set(edge.fromZone, []);
    adjacency.get(edge.fromZone)!.push(edge);
  }

  return { edges, adjacency };
}
